import { Layout } from '@components/Layout';
import { NestedSidebar } from '@components/SidebarV2';
import type { NestedSidebarItem } from '@components/SidebarV2';
import { Box } from '@mui/material';
import { Stack } from '@mui/system';
import PermissionGate from '@components/upercut-permissions/PermissionGate';
import { Navigate, Outlet, Route, Routes, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_BRAND } from '@gql/brand';
import { usePageSize } from '@hooks/usePageSize';
import { useMemo, useState } from 'react';
import BrandList from '@modules/brands/BrandList';
import BrandEdit from '@modules/brands/BrandEdit';
import BrandDetail from '@modules/brands/BrandDetail';
import BrandJobApplications from '@modules/brands/BrandJobApplications';
import BrandJobOffers from '@modules/brands/BrandJobOffers';
import BrandCardProfile from '@modules/brands/components/BrandCardProfile';
import ProfileSkeleton from '@modules/talents/components/ProfileSkeleton';

const BrandPage = () => {
    return (
        <Routes>
            <Route element={<BrandListDetailLayout />}>
                <Route
                    path="/"
                    element={
                        <PermissionGate permission="brands:read">
                            <BrandList />
                        </PermissionGate>
                    }
                />
                <Route
                    path="/:id"
                    element={<BrandDetailsLayout />}
                >
                    <Route
                        index
                        element={<Navigate to="detail" replace />}
                    />
                    <Route
                        path="detail"
                        element={
                            <PermissionGate permission="brands:read">
                                <BrandDetail />
                            </PermissionGate>
                        }
                    />
                    <Route
                        path="applications"
                        element={
                            <PermissionGate permission="job-applications:read">
                                <BrandJobApplications />
                            </PermissionGate>
                        }
                    />
                    <Route
                        path="job-offers"
                        element={
                            <PermissionGate permission="job-offers:read">
                                <BrandJobOffers />
                            </PermissionGate>
                        }
                    />
                </Route>
            </Route>
            <Route
                path="/create"
                element={
                    <PermissionGate permission="brands:create">
                        <Navigate to="/brands" replace />
                    </PermissionGate>
                }
            />
            <Route
                path="/:id/edit"
                element={
                    <PermissionGate permission="brands:update">
                        <BrandEdit />
                    </PermissionGate>
                }
            />
            <Route
                path="/:id/*"
                element={<BrandIdFallbackRedirect />}
            />
        </Routes>
    );
};

const BrandListDetailLayout = () => {
    return (
        <Layout
            breadcrumbs={[
                { label: 'Tableau de bord', href: '/', value: 'dashboard' },
                { label: 'Gestion des marques', href: '/brands', value: 'brands' },
                { label: 'Détail de la marque', href: '/brands/:id/detail', value: 'detail' },
                { label: 'Candidatures', href: '/brands/:id/applications', value: 'applications' },
                { label: "Offres d'emploi", href: '/brands/:id/job-offers', value: 'job-offers' },
            ]}
            heading="Gestion des marques"
        >
            <Outlet />
        </Layout>
    );
};

const BrandIdFallbackRedirect = () => {
    const { id } = useParams<{ id: string }>();

    if (!id) {
        return <Navigate to="/brands" replace />;
    }

    return <Navigate to={`/brands/${id}/detail`} replace />;
};

const BrandDetailsLayout = () => {
    const { id } = useParams<{ id: string }>();
    const { pageSize } = usePageSize({
        defaultPageSize: 12,
        storageKey: 'brand-details-page-size',
    });

    const [searchParams, setSearchParams] = useState({
        pagination: { page: 1, limit: pageSize },
        sortBy: { field: 'createdDate', order: -1 },
    });

    const { data: brandData, loading: loadingBrandData } = useQuery(GET_BRAND, {
        variables: { id },
        skip: !id,
    });

    const brandDetailData = useMemo(() => brandData?.getBrand, [brandData]);

    const nestedItems: NestedSidebarItem[] = useMemo(
        () => [
            { id: 'detail', label: 'Détail', icon: 'fi-rr-info', path: `/brands/${id}/detail` },
            { id: 'applications', label: 'Candidatures', icon: 'fi-br-inbox', path: `/brands/${id}/applications` },
            { id: 'job-offers', label: "Offres d'emploi", icon: 'fi-rr-briefcase', path: `/brands/${id}/job-offers` },
        ],
        [id],
    );

    if (loadingBrandData) {
        return (
            <Stack sx={{ pt: 0, flex: 1, minHeight: '100%', gap: 3 }}>
                <ProfileSkeleton />
            </Stack>
        );
    }

    return (
        <Box sx={{ display: 'flex', flex: 1, minHeight: '100%', mx: '-24px' }}>
            <NestedSidebar items={nestedItems} title="Marque" />
            <Stack flex={1} width={0} pt={0} minHeight="100%">
                <Stack sx={{ gap: 3, mb: 3, px: 3 }}>
                    <BrandCardProfile brand={brandDetailData} />
                </Stack>
                <Box sx={{ flex: 1, pb: 3, display: 'flex', px: '24px', minWidth: 0 }}>
                    <Outlet context={{ searchParams, setSearchParams }} />
                </Box>
            </Stack>
        </Box>
    );
};

export default BrandPage;
